/**
 * Boot catalog snapshot (R-B) — web consumer for connector
 * `boot_catalog_snapshot.rs` get_albums envelope + provisional action errors.
 *
 * Provisional rows are display-only until `catalog_live`. Play/open must toast
 * gently; connector may also return `provisional_catalog_action_blocked`.
 */

import { showImperativeToast } from '../providers/UIProvider';

export const PROVISIONAL_CATALOG_ACTION_BLOCKED = 'provisional_catalog_action_blocked';

export const PROVISIONAL_LIBRARY_LOADING_TOAST =
  'Your library is finishing loading — try again in a moment';

export type CatalogBootEnvelope = {
  provisional: boolean;
  updating: boolean;
  catalog_live: boolean;
  snapshot_stale: boolean;
  snapshot_captured_at?: string | null;
  snapshot_album_count?: number | null;
};

export const EMPTY_CATALOG_BOOT_ENVELOPE: CatalogBootEnvelope = {
  provisional: false,
  updating: false,
  catalog_live: false,
  snapshot_stale: false,
  snapshot_captured_at: null,
  snapshot_album_count: null,
};

function asBool(value: unknown): boolean {
  return value === true || value === 'true';
}

/** Pull envelope fields from a get_albums / index-chunk response. */
export function extractCatalogBootEnvelope(
  resp: Record<string, unknown> | null | undefined,
): CatalogBootEnvelope {
  if (!resp || typeof resp !== 'object') {
    return { ...EMPTY_CATALOG_BOOT_ENVELOPE };
  }
  const provisional = asBool(resp.provisional);
  const catalogLive = asBool(resp.catalog_live);
  const snapshotStale = asBool(resp.snapshot_stale);
  // Contract: updating is explicit; provisional paint implies updating.
  const updating = asBool(resp.updating) || (provisional && !catalogLive);
  return {
    provisional,
    updating,
    catalog_live: catalogLive,
    snapshot_stale: snapshotStale,
    snapshot_captured_at:
      typeof resp.snapshot_captured_at === 'string' ? resp.snapshot_captured_at : null,
    snapshot_album_count:
      typeof resp.snapshot_album_count === 'number'
        ? resp.snapshot_album_count
        : Number.isFinite(Number(resp.snapshot_album_count))
          ? Number(resp.snapshot_album_count)
          : null,
  };
}

export function isProvisionalAlbum(
  album: { provisional?: boolean | null } | null | undefined,
): boolean {
  return album?.provisional === true;
}

export function isProvisionalCatalogDisplayOnly(
  album: { provisional?: boolean | null } | null | undefined,
  envelope?: CatalogBootEnvelope | null,
): boolean {
  if (isProvisionalAlbum(album)) return true;
  return !!envelope?.provisional && !envelope?.catalog_live;
}

export function shouldShowCatalogUpdatingBadge(
  envelope: CatalogBootEnvelope | null | undefined,
): boolean {
  return !!envelope?.updating;
}

/** Silent live swap: provisional → catalog_live (badge drops with updating). */
export function isSilentLiveCatalogSwap(
  prev: CatalogBootEnvelope | null | undefined,
  next: CatalogBootEnvelope | null | undefined,
): boolean {
  if (!prev || !next) return false;
  return prev.provisional === true && next.catalog_live === true && next.provisional === false;
}

export function shouldRefreshForSnapshotStale(
  envelope: CatalogBootEnvelope | null | undefined,
): boolean {
  return !!envelope?.catalog_live && !!envelope?.snapshot_stale;
}

function errorText(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error ?? '');
  }
}

export type ProvisionalCatalogActionBlockedError = {
  code: typeof PROVISIONAL_CATALOG_ACTION_BLOCKED;
  provisional: boolean;
  catalog_live: boolean;
  message?: string;
};

/** Parse connector R-A blocked-action payload from IPC/HTTP error text. */
export function parseProvisionalCatalogActionBlocked(
  error: unknown,
): ProvisionalCatalogActionBlockedError | null {
  const text = errorText(error).trim();
  if (!text.includes(PROVISIONAL_CATALOG_ACTION_BLOCKED)) return null;

  const jsonStart = text.indexOf('{');
  if (jsonStart >= 0) {
    try {
      const parsed = JSON.parse(text.slice(jsonStart)) as Record<string, unknown>;
      if (String(parsed.code ?? '') === PROVISIONAL_CATALOG_ACTION_BLOCKED) {
        return {
          code: PROVISIONAL_CATALOG_ACTION_BLOCKED,
          provisional: asBool(parsed.provisional) || true,
          catalog_live: asBool(parsed.catalog_live),
          message: typeof parsed.message === 'string' ? parsed.message : undefined,
        };
      }
    } catch {
      /* fall through */
    }
  }

  return {
    code: PROVISIONAL_CATALOG_ACTION_BLOCKED,
    provisional: true,
    catalog_live: false,
  };
}

export function isProvisionalCatalogActionBlocked(error: unknown): boolean {
  return parseProvisionalCatalogActionBlocked(error) != null;
}

const TOAST_COOLDOWN_MS = 2500;
let lastToastAt = 0;

/** Gentle toast for display-only provisional tiles / blocked IPC fallback. */
export function notifyProvisionalCatalogActionBlocked(error?: unknown): void {
  const now = Date.now();
  if (now - lastToastAt < TOAST_COOLDOWN_MS) return;
  lastToastAt = now;
  const parsed = error != null ? parseProvisionalCatalogActionBlocked(error) : null;
  const message =
    (parsed?.message && parsed.message.trim())
    || PROVISIONAL_LIBRARY_LOADING_TOAST;
  showImperativeToast(message, 4000, { severity: 'info' });
}

/** Test helper — reset toast cooldown between cases. */
export function resetProvisionalCatalogToastCooldownForTests(): void {
  lastToastAt = 0;
}
